import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { IMultiSelectDropDownOption } from "../../vue-interfaces/i-grouped-multiselect";
import { useApp } from "../../../pages-vue/App/useApp";
import { IGetAgency } from "../../../services/agency.service";
import { ApplicationEnum, ApplicationNameEnum } from "../../../enums/ApplicationEnum";
import { ParentPermissionEnum } from "../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../enums/PermissionActionEnum";
import config from "../../../common/config";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { EnumsService } from "../../../services/enums.service";
import { PatientChartStatus } from "../../../enums/PatientChartStatusEnum";
import { enumToMap } from "../../../common/vue-helpers/modifiers/enum-modifiers";

import MultiSelect from "../../vue-elements/MultiSelect/MultiSelect.vue";
import BranchDefaultFilter from "../BranchDefaultFilter/BranchDefaultFilter.vue";

export default defineComponent({
    components: {
        MultiSelect,
        BranchDefaultFilter
    },
    props: {
        filtersChanged: {
            type: Function
        }
    },
    data(vm) {
        return {
            _permissionManager: null as PermissionManager,
            _enumsService: null as EnumsService,
            organizationName: "",
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            agencyList: [] as IGetAgency[],
            selectedAgencies: [] as string[],
            selectedAgenciesName: [] as string[],
            selectedBranches: [],
            patientStatusEnums: PatientChartStatus,
            selectedPatientStatus: null,
            term: "" as string,
            searchTimer: null,
            enumToMap
        };
    },

    created() {
        this._permissionManager = Container.instance.get(PermissionManager);
        this._enumsService = Container.instance.get(EnumsService);
    },

    async mounted() {
        this.organizationName = window.localStorage.getItem(config.organizationName);
        this.agencyList = this.organizations.find(
            (organization: { name: string }) => organization.name === this.organizationName
        ).agencies;
        this.agencyList = this.agencyList.map((agency) => {
            const agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            if (application == 256) {
                application = ApplicationEnum.AxxessHospice;
            }
            return {
                ...agency,
                application: application
            };
        });
        await this.setAgencyMultiSelect();
        this.selectedPatientStatus = this.patientStatusEnums.getValueUsingKey("active");
    },

    computed: {
        ...mapState(useApp, ["organizations"])
    },
    watch: {
        term: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                clearTimeout(this.searchTimer);

                this.searchTimer = setTimeout(
                    async (term) => {
                        if (term?.length > 2 || term === "") {
                            const payload = {
                                term: this.term,
                                agencies: [...this.selectedAgencies],
                                locationIds: [...this.selectedBranches],
                                patientStatus: this.selectedPatientStatus
                            };
                            await this.filtersChanged(payload);
                        }
                    },
                    500,
                    this.term
                );
            }
        },
        selectedPatientStatus: async function (newValue, oldValue) {
            if (newValue !== oldValue && this.selectedBranches.length && this.selectedAgencies.length) {
                const payload = {
                    term: this.term,
                    agencies: [...this.selectedAgencies],
                    locationIds: [...this.selectedBranches],
                    patientStatus: this.selectedPatientStatus
                };
                this.filtersChanged(payload);
            }
        }
    },

    methods: {
        async setAgencyMultiSelect() {
            try {
                this.agencyMultiSelectOptions = {};
                this.selectedAgencies.length = 0;
                if (!this.agencyList || this.agencyList?.length === 0) {
                    return;
                }
                const allowedProducts = [
                    ApplicationEnum.AgencyCore,
                    ApplicationEnum.HomeCare,
                    ApplicationEnum.AxxessHospice,
                    ApplicationEnum.AxxessPalliative
                ];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );
                const schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let application = item.application;
                        if (application === ApplicationEnum.AxxessHospiceFE) {
                            application = ApplicationEnum.AxxessHospice;
                        }
                        return allowedProducts.includes(application);
                    })
                    .map((x: any) => x.agencyId);
                const filterableProducts = this.agencyList.filter((agency) => {
                    const isAllowed = allowedProducts.includes(agency.application);
                    const agencyId = agency.id?.split("_")[0];
                    return isAllowed && schedulerAgencies.includes(agencyId);
                });
                filterableProducts?.forEach((agency) => {
                    const appName = ApplicationNameEnum[agency.application];

                    if (!this.agencyMultiSelectOptions[appName]) {
                        this.agencyMultiSelectOptions[appName] = [];
                    }

                    this.agencyMultiSelectOptions[appName].push({
                        displayText: agency.name,
                        value: agency.id
                    });
                });

                Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                    const values = agency.map((item: any) => item.value);
                    this.selectedAgencies.push(...values);
                });
                // For select agencies change
            } catch (e) {
                console.warn(e);
                // Silent fail
            }
        },
        updateBranches(branches: any) {
            this.selectedAgencies.length = 0;
            this.selectedAgencies.push(...branches);
            this.selectedAgenciesName = this.agencyList
                .filter((agency: any) => this.selectedAgencies.includes(agency.id))
                .map((agency: any) => agency.name);
            const payload = {
                term: this.term,
                agencies: [...this.selectedAgencies],
                locationIds: [...this.selectedBranches],
                patientStatus: this.selectedPatientStatus
            };
            if (this.selectedAgencies.length) {
                this.filtersChanged(payload);
            }
        },
        handleBranchesOnChange(branches: any) {
            this.selectedBranches.length = 0;
            this.selectedBranches.push(...branches);
            const payload = {
                term: this.term,
                agencies: [...this.selectedAgencies],
                locationIds: [...branches],
                patientStatus: this.selectedPatientStatus
            };
            if (branches.length) {
                this.filtersChanged(payload);
            }
        }
    }
});
